import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
} from "react";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { Empty, Splitter, Typography } from "antd";
import dayjs from "dayjs";
import { Gantt, ViewMode } from "@thepocman/gantt-task-react";
import "@thepocman/gantt-task-react/dist/style.css";
import "./timeline-library.css";
import {
  COLORS,
  DATE_FORMAT,
  getColumnWidth as getTimelineColumnWidth,
  getGanttTasksWithTodayAnchor,
  type TimelineLegendItem,
} from "./timelineUtils";
import { subjectTimelineUtils } from "./subjectTimelineUtils";
import type { TimelineItem, TimelineProps } from "./timeline";
import { useTimelineRows } from "./useTimelineRows";
import { useTimelineBarStyles } from "./useTimelineBarStyles";
import { useTimelineScroll } from "./useTimelineScroll";

const { Text } = Typography;

const ROW_HEIGHT = 40;
const HEADER_HEIGHT = 52;
const TASK_HEIGHT = 20;
const MIN_LEFT_WIDTH = 210;
const MAX_LEFT_WIDTH = 620;
const DEFAULT_LEFT_WIDTH = 340;
const MIN_TIMELINE_HEIGHT = HEADER_HEIGHT + ROW_HEIGHT;
const LEGEND_TOP_MARGIN = 16;

const toUpper = (value: string | null) => (value ? value.toUpperCase() : "—");
const formatDate = (value: string | null) =>
  value ? dayjs(value).format(DATE_FORMAT) : null;
const formatEndDate = (item: TimelineItem) =>
  item.ongoing_derived === true
    ? "Ongoing"
    : (formatDate(item.end_date) ?? "—");

function useResponsiveHeight() {
  const [viewportHeight, setViewportHeight] = useState(
    () => window.innerHeight,
  );

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return viewportHeight;
}

function useElementHeight<T extends HTMLElement>(
  elementRef: RefObject<T | null>,
  observationKey: string | number,
) {
  const [elementHeight, setElementHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      setElementHeight(elementRef.current?.getBoundingClientRect().height ?? 0);
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined" || !elementRef.current) {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      updateHeight();
    });

    observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, [elementRef, observationKey]);

  return elementHeight;
}

const renderRowCell = (item: TimelineItem, column: string) => {
  if (column === "subject" || column === "trial") {
    const value = column === "trial" ? item.trial : item.subject;
    return (
      <Text className="timeline-library-text" ellipsis={{ tooltip: value }}>
        {value ?? "—"}
      </Text>
    );
  }

  if (column === "event_type") {
    const value = toUpper(item.event_type);
    return (
      <Text className="timeline-library-text" ellipsis={{ tooltip: value }}>
        {value}
      </Text>
    );
  }

  if (column === "event_name") {
    const value = toUpper(item.event_name);
    return (
      <Text className="timeline-library-text" ellipsis={{ tooltip: value }}>
        {value}
      </Text>
    );
  }

  if (column === "start_date") {
    const value = formatDate(item.start_date);
    return (
      <Text className="timeline-library-text" ellipsis={{ tooltip: value }}>
        {value}
      </Text>
    );
  }

  if (column === "end_date") {
    const value = formatEndDate(item);
    return (
      <Text className="timeline-library-text" ellipsis={{ tooltip: value }}>
        {value}
      </Text>
    );
  }

  return null;
};

function TimelineLegend({
  legendItems,
  legendRef,
}: {
  legendItems: TimelineLegendItem[];
  legendRef: React.Ref<HTMLDivElement>;
}) {
  if (!legendItems.length) return null;

  return (
    <div
      ref={legendRef}
      className="timeline-library-legend bg-white border border-[#0000001f] p-2 rounded-lg"
    >
      {legendItems.map(({ key, label, borderColor, fillColor }) => (
        <div key={key} className="timeline-library-legend-item">
          <span
            className="timeline-library-core-dot"
            style={{
              backgroundColor: fillColor,
              borderColor: borderColor,
            }}
          />
          <span className="timeline-library-legend-label">{label}</span>
        </div>
      ))}
    </div>
  );
}

function TimelineJumpButton({
  direction,
  label,
  onClick,
  className,
}: {
  direction: "up" | "down";
  label: string;
  onClick: () => void;
  className: string;
}) {
  return (
    <button
      type="button"
      className={`timeline-library-jump-button ${className}`}
      onClick={onClick}
    >
      {direction === "up" ? (
        <ArrowUpOutlined className="timeline-library-jump-button-icon" />
      ) : (
        <ArrowDownOutlined className="timeline-library-jump-button-icon" />
      )}
      <span>{label}</span>
    </button>
  );
}

function TimelineLeftHeader({
  visibleColumns,
  columnLabels,
  gridTemplateColumns,
  minTableWidth,
  leftHeaderRef,
}: {
  visibleColumns: string[];
  columnLabels: Record<string, string>;
  gridTemplateColumns: string;
  minTableWidth: number;
  leftHeaderRef: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={leftHeaderRef}
      className="timeline-library-left-header-wrap"
      style={{ height: HEADER_HEIGHT }}
    >
      <div
        className="timeline-library-left-header"
        style={{
          height: HEADER_HEIGHT,
          gridTemplateColumns,
          minWidth: minTableWidth,
        }}
      >
        <span className="timeline-library-header-cell timeline-library-dot-header" />
        {visibleColumns.map((column) => (
          <span key={column} className="timeline-library-header-cell">
            {columnLabels[column] ?? column}
          </span>
        ))}
      </div>
    </div>
  );
}

function TimelineLeftRow({
  item,
  id,
  rowIndex,
  visibleColumns,
  gridTemplateColumns,
  minTableWidth,
  rowFillColor,
  rowBorderColor,
  isSelected,
  onClick,
}: {
  item: TimelineItem;
  id: string;
  rowIndex: number;
  visibleColumns: string[];
  gridTemplateColumns: string;
  minTableWidth: number;
  rowFillColor: string;
  rowBorderColor: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      key={id}
      className={`timeline-library-left-row ${
        isSelected ? "timeline-library-left-row--selected" : ""
      }`}
      data-testid={`timeline-row-${rowIndex}`}
      data-row-id={id}
      data-row-index={rowIndex}
      data-event-name={item.event_name}
      data-start-date={item.start_date}
      data-end-date={item.end_date ?? ""}
      data-ongoing={item.ongoing_derived === true}
      style={{
        height: ROW_HEIGHT,
        gridTemplateColumns,
        minWidth: minTableWidth,
      }}
      onClick={onClick}
    >
      <span className="timeline-library-cell timeline-library-dot-cell">
        <span
          className="timeline-library-core-dot"
          style={{
            backgroundColor: rowFillColor,
            borderColor: rowBorderColor,
          }}
        />
      </span>
      {visibleColumns.map((column) => (
        <span key={column} className="timeline-library-cell">
          {renderRowCell(item, column)}
        </span>
      ))}
    </div>
  );
}

function TimelineTooltip({ item }: { item: TimelineItem }) {
  return (
    <div className="w-56 rounded-sm border border-[#e5e7eb] bg-white p-2 text-xs shadow-[0_2px_8px_rgba(0,0,0,0.1)] wrap-break-word">
      <div>
        <strong>Event:</strong> {toUpper(item.event_name)}
      </div>
      <div>
        <strong>Type:</strong> {toUpper(item.event_type)}
      </div>
      <div>
        <strong>Start:</strong> {formatDate(item.start_date)}
      </div>
      <div>
        <strong>End:</strong> {formatEndDate(item)}
      </div>
    </div>
  );
}

export default function TimelineLibrary({
  data = [],
  viewMode: externalViewMode,
  viewDate: externalViewDate,
  centerOnDate: externalCenterOnDate,
  centerOnDateRequestId = 0,
  columns = [],
  onHorizontalScroll,
  onRowSelect,
  utils = subjectTimelineUtils,
  viewportOffset = 300,
}: TimelineProps) {
  const viewMode = externalViewMode ?? ViewMode.Month;
  const [leftWidth, setLeftWidth] = useState<number>(DEFAULT_LEFT_WIDTH);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [isTimelineDragging, setIsTimelineDragging] = useState(false);
  const leftHeaderRef = useRef<HTMLDivElement>(null);
  const leftScrollRef = useRef<HTMLDivElement>(null);
  const rightPaneRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const syncLockRef = useRef<"left" | "right" | null>(null);
  const pendingTimelineSelectionRef = useRef<string | null>(null);
  const suppressTimelineClickRef = useRef(false);

  const viewportHeight = useResponsiveHeight();

  const legendItems = useMemo(
    () =>
      utils.getAllLegendItems(
        Array.from(new Set(data.map((item) => item.core_table))).filter(
          Boolean,
        ),
      ),
    [data, utils],
  );

  const legendHeight = useElementHeight(legendRef, legendItems.length);
  const legendOccupiedHeight = legendItems.length
    ? legendHeight + LEGEND_TOP_MARGIN
    : 0;
  const maxChartHeight = Math.max(
    MIN_TIMELINE_HEIGHT,
    viewportHeight - viewportOffset - legendOccupiedHeight,
  );

  const {
    visibleColumns,
    gridTemplateColumns,
    minTableWidth,
    rows,
    tasks,
    rowsHeight,
    chartHeight,
    minimumRowDisplayed,
    itemByTaskId,
  } = useTimelineRows({
    data,
    columns,
    utils,
    rowHeight: ROW_HEIGHT,
    headerHeight: HEADER_HEIGHT,
    maxChartHeight,
  });

  const ganttTasks = useMemo(
    () => getGanttTasksWithTodayAnchor(tasks),
    [tasks],
  );

  const hasPhantomTask = ganttTasks.length > tasks.length;

  useTimelineBarStyles({
    leftHeaderRef,
    leftScrollRef,
    rightPaneRef,
    syncLockRef,
    utils,
    tasks,
    viewMode,
  });

  useTimelineScroll({
    rightPaneRef,
    tasks,
    ganttTasks,
    viewMode,
    centerOnDate: externalCenterOnDate,
    centerOnDateRequestId,
    onHorizontalScroll,
  });

  useEffect(() => {
    if (!selectedRowId) return;

    const applySelection = () => {
      const taskGroup = rightPaneRef.current?.querySelector<SVGGElement>(
        `svg[id="${selectedRowId}"] g`,
      );

      if (!taskGroup) return;

      pendingTimelineSelectionRef.current = selectedRowId;
      taskGroup.dispatchEvent(
        new MouseEvent("mousedown", { bubbles: true, cancelable: true }),
      );
      taskGroup.dispatchEvent(
        new MouseEvent("mouseup", { bubbles: true, cancelable: true }),
      );
      taskGroup.dispatchEvent(
        new MouseEvent("click", { bubbles: true, cancelable: true }),
      );
    };

    applySelection();
    const timer = window.setTimeout(applySelection, 0);

    return () => window.clearTimeout(timer);
  }, [selectedRowId, viewMode]);

  const syncGanttTaskSelection = (taskId: string) => {
    const taskGroup = rightPaneRef.current?.querySelector<SVGGElement>(
      `svg[id="${taskId}"] g`,
    );

    if (!taskGroup) return;

    pendingTimelineSelectionRef.current = taskId;

    taskGroup.dispatchEvent(
      new MouseEvent("mousedown", { bubbles: true, cancelable: true }),
    );
    taskGroup.dispatchEvent(
      new MouseEvent("mouseup", { bubbles: true, cancelable: true }),
    );
    taskGroup.dispatchEvent(
      new MouseEvent("click", { bubbles: true, cancelable: true }),
    );
  };

  const handleRowSelection = (
    rowId: string,
    item: TimelineItem,
    source: "table" | "timeline",
  ) => {
    if (
      source === "timeline" &&
      pendingTimelineSelectionRef.current === rowId
    ) {
      pendingTimelineSelectionRef.current = null;
      setSelectedRowId(rowId);
      return;
    }

    setSelectedRowId(rowId);

    if (source === "table") {
      syncGanttTaskSelection(rowId);
    }

    onRowSelect?.(item);
  };

  const getGanttRoot = () =>
    rightPaneRef.current?.querySelector<HTMLElement>(
      '[class*="_ganttTaskRoot_"]',
    );

  const scrollTimelineVertically = (position: "top" | "bottom") => {
    const scrollToTop = position === "top" ? 0 : 999999;

    leftScrollRef.current?.scrollTo({
      top: scrollToTop,
      behavior: "smooth",
    });

    getGanttRoot()?.scrollTo({
      top: scrollToTop,
      behavior: "smooth",
    });
  };

  const handleTimelineDragStart = (event: ReactMouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;

    if (target.closest(".timeline-library-jump-button")) {
      return;
    }

    const ganttRoot = getGanttRoot();
    const leftBody = leftScrollRef.current;

    if (!ganttRoot || !leftBody) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const startScrollLeft = ganttRoot.scrollLeft;
    const startScrollTop = ganttRoot.scrollTop;
    let hasDragged = false;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      if (!hasDragged && Math.abs(deltaX) + Math.abs(deltaY) > 4) {
        hasDragged = true;
        suppressTimelineClickRef.current = true;
        setIsTimelineDragging(true);
      }

      if (!hasDragged) return;

      ganttRoot.scrollLeft = startScrollLeft - deltaX;
      ganttRoot.scrollTop = startScrollTop - deltaY;
      leftBody.scrollTop = startScrollTop - deltaY;
      moveEvent.preventDefault();
    };

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      setIsTimelineDragging(false);

      window.setTimeout(() => {
        suppressTimelineClickRef.current = false;
      }, 0);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  if (!data.length) {
    return <Empty description="No timeline data available" />;
  }

  return (
    <div className="timeline-library">
      <Splitter
        style={{
          height: chartHeight,
          border: "1px solid #e5e7eb",
          borderRadius: 8,
        }}
        onResize={(sizes) => {
          const candidate = Number.parseFloat(String(sizes?.[0]));
          if (!Number.isFinite(candidate)) return;
          setLeftWidth(
            Math.min(Math.max(candidate, MIN_LEFT_WIDTH), MAX_LEFT_WIDTH),
          );
        }}
      >
        <Splitter.Panel
          min={MIN_LEFT_WIDTH}
          max={MAX_LEFT_WIDTH}
          size={leftWidth}
        >
          <div className="timeline-library-left">
            <TimelineLeftHeader
              visibleColumns={visibleColumns}
              columnLabels={utils.columnLabels}
              gridTemplateColumns={gridTemplateColumns}
              minTableWidth={minTableWidth}
              leftHeaderRef={leftHeaderRef}
            />
            <div
              ref={leftScrollRef}
              className="timeline-library-left-body"
              data-testid="timeline-left-body"
              style={{ height: rowsHeight }}
            >
              {rows.map(({ id, item, fillColor, borderColor }, index) => (
                <TimelineLeftRow
                  key={id}
                  id={id}
                  item={item}
                  rowIndex={index}
                  visibleColumns={visibleColumns}
                  gridTemplateColumns={gridTemplateColumns}
                  minTableWidth={minTableWidth}
                  rowFillColor={fillColor ?? COLORS[0]}
                  rowBorderColor={borderColor ?? COLORS[0]}
                  isSelected={selectedRowId === id}
                  onClick={() => handleRowSelection(id, item, "table")}
                />
              ))}
              {hasPhantomTask && <div style={{ height: ROW_HEIGHT }} />}
            </div>
          </div>
        </Splitter.Panel>

        <Splitter.Panel style={{ overflow: "visible" }}>
          <div
            className={`timeline-library-right-shell ${
              isTimelineDragging ? "timeline-library-right-shell--dragging" : ""
            }`}
            onMouseDown={handleTimelineDragStart}
            onClickCapture={(event) => {
              if (!suppressTimelineClickRef.current) return;
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <div
              ref={rightPaneRef}
              className="timeline-library-right"
              data-testid="timeline-gantt-pane"
              data-column-width={String(getTimelineColumnWidth(viewMode))}
            >
              <Gantt
                key={viewMode}
                tasks={ganttTasks}
                columns={[]}
                viewMode={viewMode}
                viewDate={externalViewDate ?? new Date()}
                canMoveTasks={false}
                dateFormats={{
                  monthTopHeaderFormat: "LLLL yyyy",
                }}
                TooltipContent={({ task }) => {
                  const item = itemByTaskId.get(task.id);
                  if (!item) return null;
                  return <TimelineTooltip item={item} />;
                }}
                distances={{
                  ganttHeight: chartHeight,
                  rowHeight: ROW_HEIGHT,
                  taskHeight: TASK_HEIGHT,
                  columnWidth: getTimelineColumnWidth(viewMode),
                  barFill: 52,
                  handleWidth: 0,
                  minimumRowDisplayed,
                }}
                colors={{
                  todayColor: "#ffe8e8",
                  barLabelColor: "black",
                  barLabelWhenOutsideColor: "#475569",
                }}
                fontFamily="Work Sans"
                fontSize="12"
                onClick={(task) => {
                  const item = itemByTaskId.get(task.id);
                  if (!item) return;
                  handleRowSelection(task.id, item, "timeline");
                }}
              />
            </div>
            <TimelineJumpButton
              direction="up"
              label="Jump to top"
              className="timeline-library-jump-button--top"
              onClick={() => scrollTimelineVertically("top")}
            />
            <TimelineJumpButton
              direction="down"
              label="Jump to bottom"
              className="timeline-library-jump-button--bottom"
              onClick={() => scrollTimelineVertically("bottom")}
            />
          </div>
        </Splitter.Panel>
      </Splitter>
      <TimelineLegend legendItems={legendItems} legendRef={legendRef} />
    </div>
  );
}
